import React, { useState } from 'react';
import {
  AlertCircle,
  ChevronRight,
  MapPin,
  Truck,
  Wrench,
} from 'lucide-react';
import api from '../../../services/api';
import { ExportMenu } from '../../ExportMenu';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { VirtualViolationList } from '../../VirtualViolationList';
import type {
  CarrierProfile as CarrierProfileType,
  Inspection,
  Violation,
} from '../../../types/api';

// Glow border styles for dark theme violation cards
const glowBorderStyle = (isOOS: boolean) => ({
  boxShadow: isOOS
    ? '0 0 15px rgba(179, 65, 44, 0.3), inset 0 0 8px rgba(179, 65, 44, 0.1)'
    : '0 0 15px rgba(93, 113, 72, 0.25), inset 0 0 8px rgba(93, 113, 72, 0.08)'
});

import type { ViolationsBySeverity, ViolationsSummary } from '../hooks/useViolationsSummary';
export type { ViolationsBySeverity, ViolationsSummary } from '../hooks/useViolationsSummary';

export interface ViolationsProps {
  profile: CarrierProfileType;
  violations: Violation[];
  inspections: Inspection[];
  violationsSummary: ViolationsSummary | null;
  violationsBySeverity: ViolationsBySeverity;
  loadingMore: { [key: string]: boolean };
  onLoadMoreViolations: () => void;
  setSelectedVin: (vin: string | null) => void;
  setSelectedInspection: (inspection: Inspection | null) => void;
}

const Violations: React.FC<ViolationsProps> = ({
  profile,
  violations,
  inspections,
  violationsSummary,
  violationsBySeverity,
  loadingMore,
  onLoadMoreViolations,
  setSelectedVin,
  setSelectedInspection,
}) => {
  const [expandedSeverities, setExpandedSeverities] = useState<Set<string>>(new Set());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
      {/* Export Buttons */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-sm)' }}>
        <ExportMenu
          dataType="violations"
          data={violations}
          carrierName={profile?.legalName}
          dotNumber={profile?.dotNumber}
          buttonText="Export Violations"
          size="sm"
        />
      </div>

      {/* Summary Banner */}
      <div className="card-modern fade-in-up" style={{ borderLeft: '4px solid var(--color-danger)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'flex-start' }}>
          <AlertCircle style={{ width: 24, height: 24, color: 'var(--color-danger)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--color-gray-900)', marginBottom: 'var(--space-sm)' }}>
              {violations.length} Total Violations Found
            </h4>
            <p className="text-muted" style={{ marginBottom: 'var(--space-md)' }}>
              From last 500 inspections, sorted by severity. CRITICAL and HIGH violations can result in Out-of-Service orders.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
              <div className="badge-modern badge-danger">Critical: {violationsSummary ? violationsSummary.bySeverity.critical : violationsBySeverity.CRITICAL.length}</div>
              <div className="badge-modern" style={{ background: '#efe3c8', color: '#664911' }}>High: {violationsSummary ? violationsSummary.bySeverity.high : violationsBySeverity.HIGH.length}</div>
              <div className="badge-modern badge-warning">Medium: {violationsSummary ? violationsSummary.bySeverity.medium : violationsBySeverity.MEDIUM.length}</div>
              <div className="badge-modern badge-success">Low: {violationsSummary ? violationsSummary.bySeverity.low : violationsBySeverity.LOW.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Violations by Severity */}
      {(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const).map(severity => {
        const isExpanded = expandedSeverities.has(severity);
        const displayedViolations = isExpanded
          ? violationsBySeverity[severity]
          : violationsBySeverity[severity].slice(0, 50);

        const severityColors = {
          CRITICAL: 'var(--color-danger)',
          HIGH: '#7d561c',
          MEDIUM: 'var(--color-warning)',
          LOW: 'var(--color-success)'
        };

        return violationsBySeverity[severity].length > 0 && (
          <div key={severity}>
            <h3 className="heading-3" style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              marginBottom: 'var(--space-lg)'
            }}>
              <div style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: severityColors[severity]
              }}></div>
              {severity} Severity
              <span className="text-muted" style={{ fontWeight: 400 }}>({violationsBySeverity[severity].length})</span>
            </h3>

            {/* Use VirtualViolationList for large datasets (>100 items), otherwise show regular list */}
            {violationsBySeverity[severity].length > 100 ? (
              <VirtualViolationList
                violations={violationsBySeverity[severity] as any}
                severityColor={severityColors[severity]}
                height={600}
                onVinClick={(vin) => setSelectedVin(vin)}
                onInspectionClick={async (inspId) => {
                  console.error('🚨🚨🚨 INSPECTION CLICKED:', inspId, '🚨🚨🚨');
                  // Try to find inspection in loaded array first
                  let inspection = inspections.find((insp) => {
                    const id = insp.inspectionId || (insp as any).inspection_id;
                    return String(id) === String(inspId);
                  });

                  // If not found, fetch it on-demand
                  if (!inspection) {
                    console.log('🔄 Inspection not loaded, fetching:', inspId);
                    try {
                      const response = await api.get(`/inspections/${inspId}`);
                      if (response.data) {
                        const result = response.data;
                        // Extract data from API response wrapper { success: true, data: {...} }
                        if (result.success && result.data) {
                          const rawData = result.data as any;
                          // Transform snake_case to camelCase for consistency
                          inspection = {
                            ...rawData,
                            inspectionId: rawData.inspection_id || rawData.inspectionId,
                            dotNumber: rawData.dot_number || rawData.dotNumber,
                            inspDate: rawData.insp_date || rawData.inspDate,
                            inspLevelId: rawData.insp_level_id || rawData.inspLevelId,
                            reportState: rawData.report_state || rawData.reportState,
                            reportNumber: rawData.report_number || rawData.reportNumber,
                            locationDesc: rawData.location_desc || rawData.locationDesc,
                            shipperName: rawData.shipper_name || rawData.shipperName,
                            violTotal: rawData.viol_total || rawData.violTotal,
                            oosTotal: rawData.oos_total || rawData.oosTotal,
                            driverOosTotal: rawData.driver_oos_total || rawData.driverOosTotal,
                            vehicleOosTotal: rawData.vehicle_oos_total || rawData.vehicleOosTotal,
                            hazmatOosTotal: rawData.hazmat_oos_total || rawData.hazmatOosTotal,
                            driverViolTotal: rawData.driver_viol_total || rawData.driverViolTotal,
                            vehicleViolTotal: rawData.vehicle_viol_total || rawData.vehicleViolTotal,
                            inspCarrierName: rawData.insp_carrier_name || rawData.inspCarrierName,
                          } as Inspection;
                          console.log('✅ Fetched inspection on-demand:', inspection.inspectionId);
                        } else {
                          console.error('❌ API returned error:', result);
                        }
                      }
                    } catch (error) {
                      console.error('❌ Failed to fetch inspection:', error);
                    }
                  }

                  if (inspection) {
                    setSelectedInspection(inspection);
                  } else {
                    console.warn('❌ Could not load inspection:', inspId);
                  }
                }}
              />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 'var(--space-md)' }}>
                {displayedViolations.map((v, idx) => {
                  const isOOS = v.outOfServiceIndicator === 'Y';
                  const unitTypeLabel = (() => {
                    if (v.inspViolUnit === 'D') return 'Driver';
                    if (Number(v.unitType) === 11) return 'Truck';
                    if (Number(v.unitType) === 9) return 'Trailer';
                    if (v.inspViolUnit && v.inspViolUnit !== 'D') {
                      const unitNum = parseInt(v.inspViolUnit);
                      if (v.allUnits && v.allUnits[unitNum - 1]) {
                        const unit = v.allUnits[unitNum - 1];
                        if (Number((unit as any).unitType) === 11) return 'Truck';
                        if (Number((unit as any).unitType) === 9) return 'Trailer';
                      }
                      return `Unit ${v.inspViolUnit}`;
                    }
                    return 'Vehicle';
                  })();
                  const unitInfo = v.inspViolUnit === 'D' ? 'Driver' : v.inspViolUnit ? `Vehicle ${v.inspViolUnit}` : 'Vehicle';
                  const dateField = v.inspectionDate || v.inspDate;
                  const formattedDate = dateField ? new Date(dateField).toLocaleDateString() : 'N/A';
                  const inspectionId = v.inspectionId || (v as any).inspection_id;

                  return (
                    <div
                      key={`${v.inspViolationId}-${idx}`}
                      className="relative w-full max-w-[340px] rounded-2xl overflow-hidden fade-in-up shadow-2xl"
                      style={{
                        background: '#23211d',
                        border: isOOS ? '1px solid rgba(179, 65, 44, 0.8)' : '1px solid rgba(93, 113, 72, 0.8)',
                        color: '#d3ccbc',
                        animationDelay: `${idx * 0.02}s`,
                        ...glowBorderStyle(isOOS)
                      }}
                    >
                      {/* Top Glow Effect */}
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '96px',
                          background: isOOS ? 'linear-gradient(to bottom, rgba(179, 65, 44, 0.1), transparent)' : 'linear-gradient(to bottom, rgba(93, 113, 72, 0.1), transparent)',
                          pointerEvents: 'none'
                        }}
                      ></div>

                      <div style={{ padding: '8px', position: 'relative', zIndex: 10 }}>
                        {/* Line 1: OOS + Code + Type Badge (all in one row) */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          {isOOS && (
                            <span style={{
                              background: '#b3412c',
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: '4px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                              letterSpacing: '0.025em',
                              textTransform: 'uppercase'
                            }}>
                              OOS
                            </span>
                          )}
                          <span style={{
                            color: 'white',
                            fontSize: '1.125rem',
                            fontWeight: 600,
                            letterSpacing: '0.025em'
                          }}>
                            {v.violationCode || v.violation_code || (v.partNo && v.partNoSection ? `${v.partNo}.${v.partNoSection}` : 'N/A')}
                          </span>
                          {/* Type Badge (RIGHT ALIGNED with ml-auto) */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginLeft: 'auto',
                            background: 'rgba(30, 41, 59, 0.5)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#a39d90',
                            border: '1px solid rgba(51, 65, 85, 0.5)'
                          }}>
                            <Truck size={10} />
                            <span>{unitTypeLabel}</span>
                          </div>
                        </div>

                        {/* Line 2: Category Only (no type) */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          color: '#857f73',
                          fontSize: '11px',
                          marginBottom: '12px'
                        }}>
                          <Wrench size={10} />
                          <span>{v.basicCategory || 'Unknown'}</span>
                        </div>

                        {/* Line 3: Description (NO LABEL) */}
                        <div style={{ color: '#efebe1', fontSize: '13px', fontWeight: 500, lineHeight: 1.4, marginBottom: '8px' }}>
                          {v.description || v.violation_description || 'No description available'}
                        </div>

                        {/* Line 4: VIN + Class Inline (NO LABELS) */}
                        {(v.vin || v.unitMake) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                            {v.vin && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedVin(v.vin); }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#6d685e',
                                  fontSize: '12px',
                                  fontFamily: 'monospace',
                                  letterSpacing: '0.025em',
                                  textDecoration: 'underline',
                                  textDecorationColor: 'rgba(35, 33, 29, 0.3)',
                                  textUnderlineOffset: '2px',
                                  cursor: 'pointer',
                                  padding: 0
                                }}
                              >
                                {v.vin}
                              </button>
                            )}
                            {v.unitMake && (
                              <div style={{
                                fontSize: '12px',
                                color: '#a39d90',
                                fontWeight: 500,
                                background: 'rgba(30, 41, 59, 0.5)',
                                padding: '0 6px',
                                borderRadius: '4px',
                                border: '1px solid rgba(51, 65, 85, 0.5)'
                              }}>
                                {v.unitMake}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Inspection Inner Card (darker background) */}
                        <div style={{
                          background: 'rgba(30, 41, 59, 0.6)',
                          borderRadius: '12px',
                          padding: '6px',
                          border: '1px solid rgba(51, 65, 85, 0.5)'
                        }}>
                          <div style={{
                            fontSize: '10px',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            letterSpacing: '0.05em',
                            color: '#a39d90',
                            marginBottom: '4px'
                          }}>
                            Inspection Details ({v.allUnits?.length || 1} units)
                          </div>

                          {/* Inspection # + Date on SAME LINE */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '11px', marginBottom: '2px' }}>
                            {inspectionId && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  console.error('🚨🚨🚨 [GRID] INSPECTION CLICKED:', inspectionId, '🚨🚨🚨');
                                  // Try to find inspection in loaded array first
                                  let inspection = inspections.find((insp) => {
                                    const id = insp.inspectionId || (insp as any).inspection_id;
                                    return id === inspectionId;
                                  });

                                  // If not found, fetch it on-demand
                                  if (!inspection) {
                                    console.log('🔄 Fetching inspection on-demand:', inspectionId);
                                    try {
                                      const response = await api.get(`/inspections/${inspectionId}`);
                                      if (response.data) {
                                        const result = response.data;
                                        // Extract data from API response wrapper { success: true, data: {...} }
                                        if (result.success && result.data) {
                                          const rawData = result.data as any;
                                          // Transform snake_case to camelCase for consistency
                                          inspection = {
                                            ...rawData,
                                            inspectionId: rawData.inspection_id || rawData.inspectionId,
                                            dotNumber: rawData.dot_number || rawData.dotNumber,
                                            inspDate: rawData.insp_date || rawData.inspDate,
                                            inspLevelId: rawData.insp_level_id || rawData.inspLevelId,
                                            reportState: rawData.report_state || rawData.reportState,
                                            reportNumber: rawData.report_number || rawData.reportNumber,
                                            locationDesc: rawData.location_desc || rawData.locationDesc,
                                            shipperName: rawData.shipper_name || rawData.shipperName,
                                            violTotal: rawData.viol_total || rawData.violTotal,
                                            oosTotal: rawData.oos_total || rawData.oosTotal,
                                            driverOosTotal: rawData.driver_oos_total || rawData.driverOosTotal,
                                            vehicleOosTotal: rawData.vehicle_oos_total || rawData.vehicleOosTotal,
                                            hazmatOosTotal: rawData.hazmat_oos_total || rawData.hazmatOosTotal,
                                            driverViolTotal: rawData.driver_viol_total || rawData.driverViolTotal,
                                            vehicleViolTotal: rawData.vehicle_viol_total || rawData.vehicleViolTotal,
                                            inspCarrierName: rawData.insp_carrier_name || rawData.inspCarrierName,
                                          } as Inspection;
                                          console.log('✅ Fetched inspection on-demand:', inspection.inspectionId);
                                        }
                                      }
                                    } catch (error) {
                                      console.error('❌ Failed to fetch inspection:', error);
                                    }
                                  }

                                  if (inspection) {
                                    setSelectedInspection(inspection);
                                  }
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#6d685e',
                                  textDecoration: 'underline',
                                  textUnderlineOffset: '2px',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  padding: 0,
                                  fontSize: '11px'
                                }}
                              >
                                #{inspectionId}
                              </button>
                            )}
                            <span style={{ color: '#857f73' }}>Date: <span style={{ color: '#d3ccbc' }}>{formattedDate}</span></span>
                          </div>
                          {v.inspectionState && (
                            <div style={{ fontSize: '11px', color: '#857f73' }}>
                              State: <span style={{ color: '#d3ccbc' }}>{v.inspectionState}</span>
                            </div>
                          )}

                          {/* Location */}
                          {v.inspectionLocation && (
                            <>
                              <div style={{ borderTop: '1px solid rgba(51, 65, 85, 0.5)', margin: '4px 0' }}></div>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '3px', color: '#d3ccbc', fontSize: '10px' }}>
                                <MapPin size={10} style={{ color: '#857f73', flexShrink: 0, marginTop: '2px' }} />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.8 }}>
                                  {v.inspectionLocation}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {violationsBySeverity[severity].length > 50 && (
                  <button
                    onClick={() => {
                      const newExpanded = new Set(expandedSeverities);
                      if (isExpanded) {
                        newExpanded.delete(severity);
                      } else {
                        newExpanded.add(severity);
                      }
                      setExpandedSeverities(newExpanded);
                    }}
                    className="btn-secondary"
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-sm)' }}
                  >
                    {isExpanded ? 'Show Less' : `Show All ${violationsBySeverity[severity].length} Violations (+${violationsBySeverity[severity].length - 50} more)`}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Load More Button for Violations */}
      {violations.length > 0 && (
        <button
          onClick={() => onLoadMoreViolations()}
          disabled={loadingMore['violations']}
          className="btn-secondary"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-sm)',
            marginTop: 'var(--space-lg)'
          }}
        >
          {loadingMore['violations'] ? (
            <>
              <LoadingSpinner size="sm" />
              Loading more violations...
            </>
          ) : (
            <>
              <ChevronRight size={18} />
              Load More Violations (now showing {violations.length})
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default Violations;
